import {
  createContext,
  useContext,
  PropsWithChildren,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import createAuthClient, { createAuth0Client, AuthClient } from '@appcues/auth';
import {
  setTags,
  setUserId as setUserIdForErrorReporting,
} from 'studio5/lib/error-reporting';

type AuthContextType = {
  accountId?: string;
  client: AuthClient;
  despoof: () => Promise<void>;
  isSpoofing?: boolean;
  logout: () => void;
  spoof: (id: string) => Promise<void>;
  switchAccount: (id: string) => Promise<void>;
  token?: string;
  userId?: string;
  getUserAccounts?: () => Promise<unknown[]>;
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);
const useAuth0 = import.meta.env.AUTH0_ENABLED === 'true';

export default function AuthProvider({ children }: PropsWithChildren<unknown>) {
  const authClient = useMemo(
    () =>
      useAuth0
        ? createAuth0Client({
            domain: import.meta.env.AUTH0_DOMAIN,
            clientId: import.meta.env.AUTH0_CLIENT_ID,
            audience: import.meta.env.CUSTOMER_API_URL,
            redirectUri: window.location.origin,
            baseUrl: import.meta.env.CUSTOMER_API_URL,
            crxId: 'whocares',
            scope: 'openid profile email offline_access',
          })
        : createAuthClient(
            import.meta.env.CUSTOMER_API_URL,
            import.meta.env.AUTH_APP_URL,
            import.meta.env.CRX_ID
          ),
    []
  );

  const {
    getAccountId,
    getUserId,
    getToken,
    isSpoofing: getIsSpoofing,
    getUserAccounts,
  } = authClient;

  const [accountId, setAccountId] = useState<string>();
  const [token, setToken] = useState<string>();
  const [userId, setUserId] = useState<string>();
  const [isSpoofing, setSpoofing] = useState<boolean>();

  useEffect(() => {
    getAccountId().then(setAccountId);
  }, [getAccountId]);

  useEffect(() => {
    getUserId().then(setUserId);
  }, [getUserId]);

  useEffect(() => {
    getIsSpoofing().then(setSpoofing);
  }, [getIsSpoofing]);

  useEffect(() => {
    getToken().then(setToken);
  }, [getToken]);

  useEffect(() => {
    setTags({ accountId, userId, isSpoofing });
    setUserIdForErrorReporting(userId);
  }, [accountId, userId, isSpoofing]);

  const switchAccount = useCallback(
    async (id: string) => {
      window.Appcues?.reset();
      const user = await authClient.switchAccount(id);
      return user;
    },
    [authClient]
  );

  const spoof = useCallback(
    (id: string) => {
      window.Appcues?.reset();
      return authClient.spoof(id);
    },
    [authClient]
  );

  const despoof = useCallback(() => {
    window.Appcues?.reset();
    return authClient.despoof();
  }, [authClient]);

  const logout = useCallback(() => {
    window.Appcues?.reset();
    return authClient.logout();
  }, [authClient]);

  const contextValue = useMemo(() => {
    return {
      accountId,
      client: authClient,
      token,
      despoof,
      isSpoofing,
      logout,
      spoof,
      switchAccount,
      userId,
      getUserAccounts,
    };
  }, [
    accountId,
    userId,
    isSpoofing,
    token,
    authClient,
    despoof,
    logout,
    spoof,
    switchAccount,
    getUserAccounts,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
